<template>
  <div
    class="app-image"
    :class="{ 'app-image--loaded': !!src }"
    :style="{ background: placeholderColor, aspectRatio: aspect }"
  >
    <img v-if="src" :src="src" :alt="alt" decoding="async" />
    <div v-else-if="$slots.fallback" class="app-image-fallback"><slot name="fallback" /></div>
  </div>
</template>

<script setup lang="ts">
import { onUnmounted, ref, watch } from "vue"
import { tryImageObjectUrl } from "../../services/imageService"
import { safeCssColor } from "../../utils/cssColor"

/**
 * An uploaded image, rendered by id.
 *
 * The API scope-checks every read, so the bytes come through fetch (an <img>
 * tag can't carry the bearer token) and land here as an object URL. Until they
 * do — and forever, if the image is gone or the device is offline — the tile
 * shows `avgColor`, the mean pixel the client measured at upload time. That is
 * what stops a photo surface from flashing grey boxes: the tile is the right
 * colour and the right size before a single byte arrives.
 */
const props = withDefaults(
  defineProps<{
    imageId?: string | null
    /** Mean pixel from the images row, '#rrggbb'. */
    avgColor?: string | null
    alt?: string
    /** CSS aspect-ratio for the box, e.g. "3 / 1". */
    aspect?: string
    /** Shown while nothing has loaded and there is no avgColor. */
    fallbackColor?: string
  }>(),
  { alt: "", fallbackColor: "var(--bg-secondary)" },
)

const src = ref<string | null>(null)
// Both sources are user-writable rows (images.avg_color, shopping_lists.color)
// rendered into an inline style, so they go through the colour guard — see
// utils/cssColor.
const placeholderColor = ref(safeCssColor(props.avgColor) ?? props.fallbackColor)

watch(
  () => props.avgColor,
  (color) => {
    placeholderColor.value = safeCssColor(color) ?? props.fallbackColor
  },
)

watch(
  () => props.imageId,
  async (imageId) => {
    src.value = null
    if (!imageId) return
    const url = await tryImageObjectUrl(imageId)
    // The id can change while we're fetching (fast list switching) — only
    // apply the result if it's still the one being asked for.
    if (props.imageId === imageId) src.value = url
  },
  { immediate: true },
)

onUnmounted(() => {
  src.value = null
})
</script>
